import { useCallback, useEffect, useRef, useState } from "react";

// Masjid 24/7 Home codes are Polar licence keys, one tablet per key. The tablet calls Polar's
// public licence API itself: the organisation id is public, so no secret ships in the app.
const POLAR_API = "https://api.polar.sh/v1/customer-portal/license-keys";
const POLAR_ORGANISATION = "4cca2543-5b0c-4f61-9d10-dadf747901c7";

const STORAGE_KEY = "m247-home-licence";
const DAY_MS = 24 * 60 * 60 * 1000;
// Without the internet the tablet keeps working on its last good check for this long.
const OFFLINE_ALLOWANCE_MS = 30 * DAY_MS;

export interface HomeLicence {
  key: string;
  activationId: string;
  // The last time Polar said the code was good on this tablet.
  checkedAt: number;
  // Polar said the code no longer works on this tablet: the subscription ended, or the tablet
  // was removed from the code elsewhere. Both answer the same, so the tablet doesn't guess which.
  ended?: boolean;
}

export type ActivateResult = "ok" | "notFound" | "inUse" | "ended" | "offline";

const loadLicence = (): HomeLicence | null => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
};

const saveLicence = (licence: HomeLicence | null) => {
  try {
    if (licence) localStorage.setItem(STORAGE_KEY, JSON.stringify(licence));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage blocked: the code works for this session only.
  }
};

// Keys are upper case UUIDs and Polar matches them exactly.
export const normaliseCode = (code: string) => code.replace(/\s+/g, "").toUpperCase();

const callPolar = (action: "activate" | "validate" | "deactivate", body: Record<string, string>) =>
  fetch(`${POLAR_API}/${action}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ organization_id: POLAR_ORGANISATION, ...body }),
  });

// null: no answer from Polar (offline, or Polar down), so keep the last good one.
const stillGood = async (licence: HomeLicence): Promise<boolean | null> => {
  try {
    const response = await callPolar("validate", {
      key: licence.key,
      activation_id: licence.activationId,
    });
    if (response.status === 404) return false;
    if (!response.ok) return null;
    return (await response.json()).status === "granted";
  } catch {
    return null;
  }
};

const tabletLabel = () =>
  `Tablet added ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;

export const isLicensed = (licence: HomeLicence | null) =>
  !!licence && !licence.ended && Date.now() - licence.checkedAt < OFFLINE_ALLOWANCE_MS;

// The tablet's code: entered once, checked with Polar daily, and removable so it can move to
// another tablet.
export const useHomeLicence = () => {
  const [licence, setLicenceState] = useState<HomeLicence | null>(loadLicence);
  const licenceRef = useRef(licence);
  licenceRef.current = licence;

  const setLicence = useCallback((next: HomeLicence | null) => {
    saveLicence(next);
    setLicenceState(next);
  }, []);

  const check = useCallback(async () => {
    const current = licenceRef.current;
    if (!current) return;
    const good = await stillGood(current);
    if (good === null || licenceRef.current?.key !== current.key) return;
    setLicence(good ? { ...current, checkedAt: Date.now(), ended: false } : { ...current, ended: true });
  }, [setLicence]);

  // Daily, retried hourly while Polar can't be reached.
  useEffect(() => {
    if (!licence || licence.ended) return;
    const checkIfDue = () => {
      if (Date.now() - (licenceRef.current?.checkedAt ?? 0) >= DAY_MS) check();
    };
    checkIfDue();
    const timer = window.setInterval(checkIfDue, 60 * 60 * 1000);
    window.addEventListener("online", checkIfDue);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("online", checkIfDue);
    };
  }, [licence?.key, licence?.ended, check]); // eslint-disable-line react-hooks/exhaustive-deps

  const activate = async (code: string): Promise<ActivateResult> => {
    const key = normaliseCode(code);
    // The same code again (say, re-enabled after a mistake): this tablet still holds its place,
    // and activating again would count it twice.
    const current = licenceRef.current;
    if (current?.key === key && (await stillGood(current))) {
      setLicence({ ...current, checkedAt: Date.now(), ended: false });
      return "ok";
    }
    try {
      const response = await callPolar("activate", { key, label: tabletLabel() });
      if (response.ok) {
        const activation = await response.json();
        setLicence({ key, activationId: activation.id, checkedAt: Date.now() });
        return "ok";
      }
      if (response.status === 403) {
        const { detail } = await response.json().catch(() => ({ detail: "" }));
        return /limit/i.test(detail) ? "inUse" : "ended";
      }
      return response.status === 404 || response.status === 422 ? "notFound" : "offline";
    } catch {
      return "offline";
    }
  };

  // Frees the code for another tablet. False: Polar couldn't be reached, so nothing changed.
  const remove = async (): Promise<boolean> => {
    const current = licenceRef.current;
    if (!current) return true;
    try {
      const response = await callPolar("deactivate", {
        key: current.key,
        activation_id: current.activationId,
      });
      // 404: already removed, e.g. on the Polar account page.
      if (!response.ok && response.status !== 404) return false;
    } catch {
      return false;
    }
    setLicence(null);
    return true;
  };

  return { licence, licensed: isLicensed(licence), activate, remove };
};

export type HomeLicenceState = ReturnType<typeof useHomeLicence>;
